// cleanup_convert_to_glu_component_SMB_EMPLOYEE_PAYMENT
// cleanup_hoc_to_hooks_SMB_EMPLOYEE_PAYMENT
// cleanup_convert_to_typescript_file_SMB_EMPLOYEE_PAYMENT

import React, { useEffect, useState, lazy, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { useValidation } from '@glu/validation-react';
import { UseFundsNextIcon } from '@glu/icons-react';
import Alert from '@glu/alerts-react';

import useLocale from 'components/useLocale';
import Workflow from 'components/workflow/Workflow';
import Spinner from 'components/spinner/Spinner';
import ErrorBoundary from 'components/errorBoundary/ErrorBoundary';
import Prompt from 'components/Prompt/PromptComponent';
import ModalNotification from 'components/modalNotification/ModalNotification';
import ChallengeManager from 'components/challengeManager/ChallengeManager';
import { ALERTVARIATIONS, APPROVE, SUBMIT, URL_PARAM_ACTIONS, WORKSPACES } from 'constants.js';
import { setReduxMessage } from 'logic/store/messages/actions';
import { setSmbHighlightedRows } from 'logic/store/payments/actions';
import { numberToCurrency } from 'utils/currency';
import { areObjectsEqual } from 'utils/objectUtils';

import { loadData, loadPaymentData, runOnSubmit } from './employeePayments.api';
import useStyles from './employeePayments.styles';
import Footer from './Footer';
import { currencyToNumber, getNumberOfTransactions } from './shared';

const { ERROR, SUCCESS } = ALERTVARIATIONS;
const { COPY, CREATE, MODIFY, VIEW } = URL_PARAM_ACTIONS;
const { PYMTCNTR } = WORKSPACES;

const EmployeePaymentForm = lazy(() =>
  import(/* webpackChunkName: 'EmployeePaymentForm' */ './EmployeePaymentForm')
);

const getTitle = (action, locale) => {
  switch (action) {
    case CREATE:
    case COPY:
      return locale['employee.payment.title.initiate'];
    case MODIFY:
      return locale['employee.payment.title.modify'];
    case VIEW:
      return locale['employee.payment.title.view'];
    default:
      return '';
  }
};

const EmployeePayments = ({ setReduxMessage, setSmbHighlightedRows, userSessionInfo }) => {
  const { action, id } = useParams();
  const initialState = useRef();
  const [FormProvider, formContext, { data: values, onChange, validate }] = useValidation();
  const classes = useStyles();
  const { actionAlert, challengeManagerContainer, currentAmount } = classes;
  const locale = useLocale();
  const history = useHistory();
  const [APIData, setAPIData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // This has the route guard for back arrow and cancel button
  // It will also include confirmation for some actions
  const [confirmation, setConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(false);
  // Used for internal page and modal success/error messaging
  const [message, setMessage] = useState(null);
  const [challengeManager, setChallengeManager] = useState(false);

  const title = getTitle(action, locale);
  // Number of transactions is equal to the payee's who's amount is not 0
  const numberOfTransactions = getNumberOfTransactions(values.transactions);
  // User shouldn't submit when all amounts on transactions are 0
  const canSubmit = !isSubmitting && numberOfTransactions !== 0;
  const historyLocation = history?.location?.state?.previous;

  useEffect(() => {
    if (APIData && values && !initialState.current) {
      initialState.current = values;
    }
  }, [APIData, values]);

  const reloadData = () =>
    loadData({
      action,
      setAPIData,
      onChange,
      custTraceNum: id
    });

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasFormChanged = () => {
    const { batch: batchInitialState } = initialState.current || {};
    const { batch: batchFormData } = values || {};

    const newInitialState = {
      ...initialState.current,
      batch: {
        ...batchInitialState,
        fromAccount: batchInitialState?.fromAccount?.map(({ id }) => ({ accountId: id })),
        entity: batchInitialState?.entity?.map(({ id }) => ({ id }))
      }
    };
    const updatedFormData = {
      ...values,
      batch: {
        ...batchFormData,
        fromAccount: batchFormData?.fromAccount?.map(({ accountId }) => ({ accountId })),
        entity: batchFormData?.entity?.map(({ id }) => ({ id }))
      }
    };

    return !areObjectsEqual(newInitialState, updatedFormData);
  };

  const updateACHSelection = (id) => {
    const selectedRecord = APIData?.achCompanies.find((company) => company.achSenderId === id);
    setAPIData((prev) => ({
      ...prev,
      achCompany: selectedRecord
    }));
  };

  // Used to revert changes from 'Undo Changes' button on modify flow
  const undoChanges = () =>
    loadPaymentData({
      dateSettings: APIData?.dateSettings,
      onChange,
      paymentDetails: APIData?.paymentDetails,
      payees: APIData?.payees
    });

  const totalAmount = values.transactions
    ? Object.keys(values.transactions).reduce((previous, current) => {
        const transactionAmount = currencyToNumber(values.transactions[current].amount);
        return previous + transactionAmount;
      }, 0)
    : 0;

  const onSubmit = (submitType) => {
    setIsSubmitting(true);
    runOnSubmit({
      action,
      APIData,
      confirmation,
      history,
      id,
      locale,
      onChange,
      setChallengeManager,
      setConfirmation,
      setIsSubmitting,
      setMessage,
      setReduxMessage,
      setSmbHighlightedRows,
      submitType,
      totalAmount,
      userSessionInfo,
      values,
      classes,
      hasFormChanged: hasFormChanged()
    });
  };

  const possibleActions = APIData?.paymentDetails?.possibleActions || [];

  const props = {
    action,
    APIData,
    history,
    locale,
    onChange,
    onSubmit,
    possibleActions,
    undoChanges,
    validate,
    values,
    id,
    updateACHSelection
  };
  if (!APIData) {
    return (
      <Workflow
        extra={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <div className={currentAmount}>
            <span aria-live="polite" className="amount">
              {locale['common.loading']}
            </span>
          </div>
        }
        onBack={() =>
          history.push({
            pathname: PYMTCNTR,
            state: {}
          })
        }
        title={title}
      >
        <Spinner />
      </Workflow>
    );
  }

  return (
    <Workflow
      extra={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <div className={currentAmount}>
          <span aria-live="polite" className="amount">
            {numberToCurrency(totalAmount)}
          </span>
          <span className="subtext">{`(${numberOfTransactions}) ${locale['common.payment']}`}</span>
        </div>
      }
      onBack={() => {
        if (action === VIEW || !APIData.achCompany || !hasFormChanged()) {
          history.push({
            pathname: historyLocation || PYMTCNTR
          });
        } else {
          setConfirmation({
            callback: () =>
              history.push({
                pathname: historyLocation || PYMTCNTR
              }),
            routeGuard: true
          });
        }
      }}
      title={title}
    >
      {challengeManager && (
        <div className={challengeManagerContainer}>
          <ChallengeManager
            challengeErrorMessage={Boolean(challengeManager.data.numberOfFailedAttempts)}
            data={challengeManager.data}
            onCancel={() => {
              setChallengeManager(null);
              setConfirmation(false);
            }}
            onHide={() => {
              setChallengeManager(null);
              setConfirmation(false);
            }}
            onSubmit={() => challengeManager.callback()}
            open
          />
        </div>
      )}
      <div>
        {!APIData?.achCompanies?.length ? (
          <p>{locale['employee.payment.noCompany']}</p>
        ) : (
          <>
            {message && (
              <Alert
                className={actionAlert}
                dataQa="action-alert"
                message={message.title}
                onHide={() => setMessage(null)}
                variant={message.success ? SUCCESS : ERROR}
              />
            )}
            {confirmation && (
              <>
                {confirmation.routeGuard ? (
                  <Prompt
                    onCancel={() => {
                      setConfirmation(false);
                      setIsSubmitting(false);
                      setChallengeManager(false);
                    }}
                    onConfirm={() => confirmation.callback()}
                  />
                ) : (
                  <ModalNotification
                    icon={
                      [APPROVE, SUBMIT].includes(confirmationAction) ? UseFundsNextIcon : false
                    }
                    onHide={() => {
                      setConfirmation(false);
                      setIsSubmitting(false);
                      setChallengeManager(false);
                    }}
                    show
                    suppressIcon={false}
                    variant={confirmation.variant || (confirmation.success ? SUCCESS : ERROR)}
                  >
                    {confirmation.Component}
                  </ModalNotification>
                )}
              </>
            )}
            <ErrorBoundary>
              <FormProvider value={formContext}>
                <EmployeePaymentForm {...props} />
              </FormProvider>
            </ErrorBoundary>
          </>
        )}
      </div>
      <Footer
        action={action}
        amount={totalAmount}
        APIData={APIData}
        canSubmit={canSubmit}
        locale={locale}
        numPayments={numberOfTransactions}
        onSubmit={onSubmit}
        possibleActions={possibleActions}
        setConfirmationAction={setConfirmationAction}
        setMessage={setMessage}
        setSmbHighlightedRows={setSmbHighlightedRows}
        validate={validate}
        values={values}
      />
    </Workflow>
  );
};

EmployeePayments.propTypes = {
  setSmbHighlightedRows: PropTypes.func.isRequired,
  setReduxMessage: PropTypes.func.isRequired,
  userSessionInfo: PropTypes.shape({}).isRequired
};

const mapStateToProps = ({ auth: { user } = {} } = {}) => ({
  userSessionInfo: user
});

const mapDispatchToProps = {
  setReduxMessage,
  setSmbHighlightedRows
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeePayments);
